/**
 * Stable identifiers for studies — natural key, canonical content hash, and
 * derived id. Shared between the in-app bulk importer and the Node sync CLI
 * so both produce the exact same study ids for the same source content.
 *
 * Uses globalThis.crypto.subtle (available in browsers and Node 19+).
 */
function locationKey(loc) {
    return `${loc.testamentIndex}/${loc.bookIndex}/${loc.chapterIndex}/${loc.verseIndex}`;
}
export function studyNaturalKey(study) {
    return `${study.name.trim().toLowerCase()}::${locationKey(study.startingLocation)}`;
}
async function sha256Hex(text) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
function canonicalize(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalize).join(',')}]`;
    const obj = value;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalize(obj[k])}`).join(',')}}`;
}
export async function studyContentHash(study) {
    const canonical = canonicalize({
        name: study.name,
        startingLocation: study.startingLocation,
        endingLocation: study.endingLocation,
        writingLocation: study.writingLocation,
        memoryStartingLocation: study.memoryStartingLocation,
        memoryEndingLocation: study.memoryEndingLocation,
        scenes: study.scenes,
        assignments: study.assignments?.map((a) => ({
            studentId: a.student.id,
            reviewQuestion: a.reviewQuestion,
            startingLocation: a.startingLocation,
            endingLocation: a.endingLocation,
        })),
        teacherAssignments: study.teacherAssignments?.map((t) => ({
            teacherId: t.teacher.id,
            reviewQuestion: t.reviewQuestion,
        })),
    });
    return sha256Hex(canonical);
}
/** UUID-shaped deterministic id derived from the natural key. */
export async function deriveStudyId(study) {
    const hex = await sha256Hex(studyNaturalKey(study));
    return (`${hex.slice(0, 8)}-${hex.slice(8, 12)}-` +
        `${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`);
}
//# sourceMappingURL=study-hash.js.map